/**
 * StepPicker (BLU-1376, handover 4.1, D1): the phone stand-in for the rail.
 *
 * Under ~760px of container width the rail is hidden and this full-width
 * button ("Step 3 of 8: Tag products") sits above the pane. It opens the same
 * rail inline, below itself (no popover, so nothing floats over the pane or
 * scrolls sideways at 390px); picking a step closes it.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StepId } from '../../lib/steps';
import { SetupRail, type SetupRailItem } from './SetupRail';
import { StepBadge } from './StepBadge';

interface StepPickerProps {
  items: readonly SetupRailItem[];
  selectedId: StepId | null;
  onSelect: (id: StepId) => void;
  className?: string;
}

export function StepPicker({ items, selectedId, onSelect, className }: StepPickerProps) {
  const { t } = useTranslation('onboarding');
  const [open, setOpen] = useState(false);
  const listId = useId();
  const selected = items.find((item) => item.id === selectedId) ?? items[0];

  if (!selected) return null;

  return (
    <div className={cn('w-full', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex min-h-11 w-full items-center gap-2.5 rounded-lg border border-line-strong bg-card px-3 py-2 text-left',
          'transition-colors duration-150 ease-out motion-reduce:transition-none hover:border-threadheads-purple/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        <StepBadge number={selected.number} state={selected.state} size="rail" />
        <span className="min-w-0 flex-1 text-sm font-medium leading-5 text-foreground">
          {t('picker.label', { number: selected.number, total: items.length, label: selected.label })}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div id={listId} className="mt-2 rounded-lg border border-line-strong bg-card p-1.5 motion-safe:animate-fade-in">
          <SetupRail
            variant="inline"
            items={items}
            selectedId={selected.id}
            onSelect={(id) => {
              setOpen(false);
              onSelect(id);
            }}
          />
        </div>
      )}
    </div>
  );
}
